import * as ReactSelect from '@radix-ui/react-select'
import { useState } from 'react'
import clsx from 'clsx'

// Components and icons
import { ChevronDown, DropdownTriangle, Plus } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { SelectItem } from '@/components/select/SelectItem'
import { Tag } from '@/components/tag/Tag'

// Types
import type { SelectProps } from '@/components/select/types'

const CREATE_ACTION_VALUE = 'CREATE_ACTION'

/**
 * SelectDropdown - Generic Dropdown Select Component
 *
 * A flexible dropdown component built on Radix UI Select with full keyboard
 * navigation and accessibility support.
 *
 * ## Variants
 *
 * - **field**: Bordered dropdown styling (default)
 * - **inline**: Compact button-style trigger for inline usage
 *
 * ## Features
 *
 * - Keyboard navigation (Arrow keys, Enter, Escape)
 * - Screen reader support with ARIA-compliant announcements
 * - Optional "Create New" action button at the bottom
 *
 * ## Key Props
 * - `items`: Array of items to display in the dropdown
 * - `selectedItemId`: The ID of the currently selected item
 * - `onSelect`: Callback function to handle item selection
 * - `placeholder`: Text to display when no item is selected
 * - `variant`: The variant of the dropdown
 * - `createAction`: Optional create action configuration
 * - `emptyText`: Text to display when there are no items
 * - `emptyStateMessage`: Message shown in a styled box when there are no items
 *
 */
export function Select({
  ariaLabel = 'Select a value',
  arrowIcon = 'DropdownTriangle',
  className,
  createAction,
  emptyText = '',
  emptyStateMessage = '',
  fullWidthContent = false,
  items,
  menuItemsHeading = '',
  onSelect,
  placeholder = 'Select',
  selectedItemId,
  size = 'medium',
  triggerBadge,
  triggerLabel,
  variant = 'field',
  ...props
}: SelectProps) {
  const [open, setOpen] = useState(false)
  // Key to force remount after create action, resetting Radix's internal state
  const [selectKey, setSelectKey] = useState(0)

  const handleItemSelect = async (itemId: string) => {
    // Handle create action
    if (itemId === CREATE_ACTION_VALUE) {
      setOpen(false)
      // Force remount to reset Radix's internal state that incorrectly tracks CREATE_ACTION_VALUE
      setSelectKey((prev) => prev + 1)
      createAction?.onClick()
      return
    }

    // Skip if already selected
    if (itemId === selectedItemId) {
      setOpen(false)
      return
    }

    // Call the onSelect handler (can be sync or async)
    await onSelect(itemId)

    // Close dropdown after selection
    setOpen(false)
  }

  const selectedItem = selectedItemId ? items.find((item) => item.id === selectedItemId) : null
  const selectedLabel = triggerLabel ?? selectedItem?.label ?? placeholder

  // Separate regular items from sticky items
  const regularItems = items.filter((item) => !item.sticky)
  const stickyItems = items.filter((item) => item.sticky)

  // Default icon for create action is Plus
  const CreateActionIcon = createAction?.icon ?? Plus

  return (
    <ReactSelect.Root
      key={selectKey}
      value={selectedItemId ?? undefined}
      onValueChange={handleItemSelect}
      open={open}
      onOpenChange={setOpen}
      aria-label={ariaLabel}
      {...props}
    >
      {variant === 'inline' && (
        <ReactSelect.Trigger asChild aria-label={ariaLabel}>
          <Button
            variant="plain"
            size="sm"
            className="group data-[state=open]:bg-brand-100"
            trailingIcon={
              arrowIcon === 'ChevronDown' ? (
                <ChevronDown className="w-4 h-4 text-brand-500 transition-transform duration-300 group-data-[state=open]:rotate-180" />
              ) : (
                <DropdownTriangle className="w-4 h-4" />
              )
            }
          >
            <span className="truncate select-none text-sm font-medium">{selectedLabel}</span>
          </Button>
        </ReactSelect.Trigger>
      )}

      {/*
        Only `field` is tagged. The `inline` variant above wraps a `<Button>` via `Trigger asChild`
        (Button is already tagged), and one DOM element can't hold two `data-ds-component` values —
        so inline Selects inspect as "Button" by design.
      */}
      {variant === 'field' && (
        <ReactSelect.Trigger
          data-ds-component="Select"
          data-ds-variant={variant}
          data-ds-size={size}
          className={clsx(
            'flex w-full items-center justify-between gap-2 text-brand-neutral-600 rounded border border-brand-neutral-300 py-1.5 pl-3 pr-2 focus:outline-none',
            size === 'medium' && 'h-10',
            size === 'large' && 'h-14',
            triggerBadge ? 'bg-yellow-50' : 'bg-white'
          )}
          aria-label={ariaLabel}
        >
          <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
            <span className={clsx('truncate select-none', size === 'medium' && 'text-sm font-medium', size === 'large' && 'text-base font-normal')}>
              {selectedLabel}
            </span>
            {triggerBadge && <Tag>{triggerBadge}</Tag>}
          </div>

          <ReactSelect.Icon>
            {arrowIcon === 'ChevronDown' ? (
              <ChevronDown className={size === 'large' ? 'h-6 w-6' : 'h-4 w-4'} />
            ) : (
              <DropdownTriangle className={size === 'large' ? 'h-6 w-6' : 'h-4 w-4'} />
            )}
          </ReactSelect.Icon>
        </ReactSelect.Trigger>
      )}

      <ReactSelect.Portal>
        <ReactSelect.Content
          position="popper"
          sideOffset={8}
          style={{
            maxHeight: 'calc(var(--radix-select-content-available-height) - 8px)',
            width: fullWidthContent ? 'var(--radix-select-trigger-width)' : undefined,
          }}
          className={clsx(
            'z-50 flex flex-col overflow-hidden rounded-md border border-brand-neutral-200 bg-white shadow-menu radix-select-fade-slide',
            fullWidthContent ? 'w-full' : 'w-[260px]',
            className
          )}
        >
          {/* Viewport to allows scrolling of the items */}
          <ReactSelect.Viewport className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {/* Scrollable: only regular items */}
            {(regularItems.length > 0 || !!emptyText || !!emptyStateMessage) && (
              <div className="min-h-0 flex-1 overflow-y-auto p-2 space-y-2">
                {menuItemsHeading && !!regularItems.length && (
                  <div className="px-1">
                    <div className="flex p-2 justify-start items-center gap-2.5 self-stretch">
                      <h3 className="text-xs font-semibold uppercase leading-4 text-brand-neutral-500">{menuItemsHeading}</h3>
                    </div>
                  </div>
                )}

                {regularItems.length > 0 ? (
                  regularItems.map((item) => (
                    <SelectItem key={item.id} value={item.id} label={item.label} badge={item.badge} description={item.description}>
                      {item.label}
                    </SelectItem>
                  ))
                ) : (
                  <>
                    {emptyStateMessage && (
                      <div className="flex w-full px-2">
                        <div
                          className="flex px-3 py-3 items-center gap-3 flex-1 rounded-lg border border-brand-neutral-200 bg-brand-neutral-50 text-xs text-brand-neutral-500"
                          data-non-selectable-description
                        >
                          {emptyStateMessage}
                        </div>
                      </div>
                    )}
                    {emptyText && <p className="text-sm text-brand-neutral-600 p-2 space-y-2 text-center">{emptyText}</p>}
                  </>
                )}
              </div>
            )}

            {/* Sticky items are fixed at bottom */}
            {(stickyItems.length > 0 || createAction) && (
              <div className={clsx('flex-shrink-0 bg-white', (!!regularItems.length || !!emptyText || !!emptyStateMessage) && 'border-t')}>
                {!!stickyItems.length && (
                  <div className="p-2">
                    {stickyItems.map((item) => (
                      <SelectItem key={item.id} value={item.id} label={item.label} badge={item.badge} description={item.description}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </div>
                )}

                {createAction && (
                  <div className={clsx('p-2', stickyItems.length > 0 && 'border-t')}>
                    <ReactSelect.Item
                      value={CREATE_ACTION_VALUE}
                      className="flex w-full cursor-pointer items-center gap-1 rounded px-2 py-1.5 text-sm font-semibold text-brand-500 outline-none hover:bg-brand-neutral-100 focus:bg-brand-neutral-100 data-[highlighted]:bg-brand-neutral-100"
                    >
                      <CreateActionIcon className="h-[18px] w-[18px]" aria-hidden />
                      <ReactSelect.ItemText>{createAction.label}</ReactSelect.ItemText>
                    </ReactSelect.Item>
                  </div>
                )}
              </div>
            )}
          </ReactSelect.Viewport>
        </ReactSelect.Content>
      </ReactSelect.Portal>
    </ReactSelect.Root>
  )
}
